import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { iconSize } from '@munifocus/tokens';
import {
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '../primitives';
import * as Icons from '../icons';
import type { DataTableColumn } from './DataTable';

/**
 * Whether the row actions around here have the room to say their names.
 *
 * `DataTable` turns this on when its columns, labels and all, are wider than
 * the card: a contracts table at a 1240px laptop used to push its last action
 * past the edge ("E…") and give the card a scrollbar. Compact, each action
 * keeps its icon and moves its name into the tooltip and the accessible name,
 * so the column shrinks to a strip of buttons instead of running off-screen.
 * Outside a table nothing provides it, and actions keep their labels.
 */
export const RowActionsCompactContext = createContext(false);

/** Every icon inside a row's action cell. One step down from a toolbar's. */
export const ROW_ICON_SIZE = iconSize.sm;

export type RowActionTone = 'default' | 'danger';

export interface RowActionProps {
  label: string;
  /** The `Icons.*` component, not an element — the size is not the caller's to pick. */
  icon?: LucideIcon;
  onClick: () => void;
  tone?: RowActionTone;
  disabled?: boolean;
  /**
   * Always behind "More", however much room the cell has.
   *
   * For an action that should never be one slip away from the row's main one:
   * a Delete set inline beside Send invites the wrong click, and a destructive
   * action is the last thing a row should offer at a glance. Menu-only actions
   * sit at the end of the menu.
   */
  menu?: boolean;
  /** Extra context on hover, e.g. when a bulletin was last sent. */
  tooltip?: string;
}

/**
 * One button inside a row's action cell.
 *
 * **It stops propagation, always.** These sit inside a row that is itself
 * clickable. Without it a click both runs the action *and* fires the row's own
 * handler — at best navigating twice, at worst revoking access and then opening
 * the record you were about to check first.
 *
 * The page-specific actions used to be written by hand next to `<RowActions>`,
 * and every page wrote them differently: `variant="text"` here, the default
 * there, `color="error"` on one destructive action and plain on the next, icons
 * at 14px, 15px, 16px and none at all. This is the same control every time.
 */
export function RowAction({
  label,
  icon: Icon,
  onClick,
  tone = 'default',
  disabled,
  tooltip,
}: RowActionProps) {
  const compact = useContext(RowActionsCompactContext);
  // An action with no icon has nothing to shrink to; it keeps its words.
  if (compact && Icon) {
    return (
      <Tooltip title={tooltip ? `${label} — ${tooltip}` : label}>
        <span>
          <IconButton
            size="small"
            color={tone === 'danger' ? 'error' : 'primary'}
            disabled={disabled}
            aria-label={label}
            onClick={(event: MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <Icon size={ROW_ICON_SIZE} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
  const button = (
    <Button
      size="small"
      variant="text"
      color={tone === 'danger' ? 'error' : 'primary'}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      startIcon={Icon ? <Icon size={ROW_ICON_SIZE} /> : undefined}
      sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
    >
      {label}
    </Button>
  );
  if (!tooltip) return button;
  // The span keeps the tooltip working on a disabled button, which fires no
  // pointer events of its own.
  return (
    <Tooltip title={tooltip}>
      <span>{button}</span>
    </Tooltip>
  );
}

export interface RowActionsProps {
  /** Open the record. Omit only when there is nothing to open. */
  onView?: () => void;
  /** Edit the record, when it has an editor of its own. */
  onEdit?: () => void;
  /** Anything page-specific — a revoke, a restore, a resend. Use `RowAction`. */
  children?: ReactNode;
}

/**
 * The actions cell at the end of a list row.
 *
 * **Why every list needs one.** Ten of the portal's tables opened a record on a
 * row click and showed nothing to say so. A clickable row is a convention people
 * have to already know, and an operator scanning a table for how to open
 * something should not have to discover it by guessing — half of them never do,
 * and the other half learn it on one page and find the next page works
 * differently.
 */
/**
 * How many actions fit in the cell before the rest go behind "More".
 *
 * A contract row can carry View, Edit, Submit, Sign, Decline, Suspend and
 * Terminate depending on its state and the caller's powers. Laid out in a row
 * they ran off the right edge of the table at ordinary laptop widths, and the
 * action that fell off was usually the one the row was waiting for.
 */
export const MAX_INLINE_ROW_ACTIONS = 3;

export function RowActions({ onView, onEdit, children }: RowActionsProps) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const actions: RowActionProps[] = [
    ...(onView ? [{ label: t('common.actions.view'), icon: Icons.Eye, onClick: onView }] : []),
    ...(onEdit ? [{ label: t('common.actions.edit'), icon: Icons.Pencil, onClick: onEdit }] : []),
  ];
  const others: ReactNode[] = [];
  for (const child of Children.toArray(children)) {
    if (isValidElement(child) && child.type === RowAction) {
      actions.push((child as ReactElement<RowActionProps>).props);
    } else {
      // Anything that is not a RowAction is the page's own control; it stays
      // inline, as it always did.
      others.push(child);
    }
  }

  const inlineable = actions.filter((action) => !action.menu);
  const menuOnly = actions.filter((action) => action.menu);
  // "More" takes a slot of its own whenever it is on screen.
  const capacity =
    menuOnly.length > 0 || inlineable.length > MAX_INLINE_ROW_ACTIONS
      ? MAX_INLINE_ROW_ACTIONS - 1
      : MAX_INLINE_ROW_ACTIONS;
  const inline = inlineable.slice(0, capacity);
  const overflow = inlineable.slice(capacity);
  const hidden = [...overflow, ...menuOnly];

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'nowrap' }}
    >
      {inline.map((action) => (
        <RowAction key={action.label} {...action} />
      ))}
      {others}
      {hidden.length > 0 && (
        <>
          {/* Icon-only, named by its aria-label and tooltip: a worded "More"
              button cost the actions column the width that
              clipped it at a 1280px laptop. */}
          <Tooltip title={t('common.actions.more')}>
            <IconButton
              size="small"
              color="primary"
              aria-label={t('common.actions.more')}
              aria-haspopup="menu"
              aria-expanded={Boolean(anchor)}
              onClick={(event: MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                setAnchor(event.currentTarget);
              }}
            >
              <Icons.MoreVertical size={ROW_ICON_SIZE} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            onClick={(event: MouseEvent) => event.stopPropagation()}
          >
            {hidden.flatMap((action, index) => {
              const Icon = action.icon;
              // A hairline between the ordinary overflow and the menu-only
              // tail, so a Delete never reads as one more item in the list.
              const divider =
                index > 0 && index === overflow.length ? [<Divider key="menu-only" />] : [];
              return [
                ...divider,
                <MenuItem
                  key={action.label}
                  disabled={action.disabled}
                  onClick={() => {
                    setAnchor(null);
                    action.onClick();
                  }}
                  sx={action.tone === 'danger' ? { color: 'error.main' } : undefined}
                >
                  {Icon && (
                    <ListItemIcon sx={{ color: 'inherit' }}>
                      <Icon size={ROW_ICON_SIZE} />
                    </ListItemIcon>
                  )}
                  {action.label}
                </MenuItem>,
              ];
            })}
          </Menu>
        </>
      )}
    </Stack>
  );
}

/**
 * The actions column's header, alignment and picker rule — everything about it
 * that is not the buttons themselves. Spread it into the column:
 *
 * ```tsx
 * { ...actionsColumn, cell: (row) => <RowActions onView={...} /> }
 * ```
 *
 * Written out by hand it drifted six ways: the column was headed "Actions" from
 * five different translation keys and left blank twice, two tables let the
 * column picker hide the only way into a record, and one forgot to right-align
 * so the buttons floated in the middle of a wide cell. There is nothing
 * page-specific about any of that, so no page decides it any more — not even
 * the header text, which is why this is a hook and not a constant.
 *
 * It is spread rather than called because a function returning the whole column
 * would have to infer the row type from its own callback, and inside a
 * `columns` array there is nothing to infer it from: every cell argument came
 * back `unknown`. Spreading leaves `cell` where the array can still type it.
 *
 * The return type is a `Pick` rather than an `Omit` for the same reason. `Omit`
 * keeps `sortValue`, which mentions the row type, and a spread carrying it
 * pins the whole array's row to whatever this hook said — which is nothing.
 */
export type RowActionsColumn = Pick<
  DataTableColumn<never>,
  'key' | 'label' | 'align' | 'alwaysVisible' | 'sticky'
>;

export function useRowActionsColumn(): RowActionsColumn {
  const { t } = useTranslation();
  return {
    key: 'actions',
    label: t('common.actions.label'),
    align: 'right',
    alwaysVisible: true,
    // Pinned right: on a table wider than its card, the actions stay in reach
    // while the data columns scroll beneath them.
    sticky: true,
  };
}
